/** PRNG déterministe (mulberry32) : une graine → une suite reproductible. */
function mulberry32(seed) {
    let a = seed >>> 0;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Empreinte entière stable d'un scénario, servant de graine quand aucune n'est fournie. */
function hashScenario(s) {
    const str = `${s.environment}|${s.timeOfDay}|${s.sky}|${s.altitudeM}|${s.gimbalPitchDeg}|${s.targets
        .map((t) => `${t.label}:${t.density}`)
        .join(',')}`;
    let h = 2166136261;
    for (let i = 0; i < str.length; i += 1) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}
const SKY_BY_TIME = {
    DAWN: { top: '#243350', bottom: '#e6a066', ambient: 0.62 },
    DAY: { top: '#4a86c5', bottom: '#aacbe8', ambient: 1 },
    DUSK: { top: '#33253f', bottom: '#d67a4f', ambient: 0.5 },
    NIGHT: { top: '#0a0f1e', bottom: '#182238', ambient: 0.26 },
};
const GROUND_BY_ENV = {
    FIELD: '#6d7d3f',
    FOREST: '#2f4a2b',
    DENSE_HABITAT: '#4b4b52',
    PERIURBAN: '#5a6a48',
};
const HAZE_BY_SKY = {
    CLEAR: 0,
    PARTLY_CLOUDY: 0.12,
    OVERCAST: 0.32,
    FOG: 0.55,
};
/** Nombre indicatif d'éléments de décor par environnement. */
const FEATURE_COUNT = {
    FIELD: 5,
    FOREST: 22,
    DENSE_HABITAT: 14,
    PERIURBAN: 13,
};
const clamp01 = (v) => Math.min(1, Math.max(0, v));
export function deriveScenarioPreview(scenario) {
    const rand = mulberry32(scenario.seed ?? hashScenario(scenario));
    // Cadrage : plus le tangage tend vers le nadir (90°), plus le sol occupe l'image (moins de ciel).
    const horizonY = clamp01(0.35 + (scenario.gimbalPitchDeg / 90) * 0.65);
    const skyPal = SKY_BY_TIME[scenario.timeOfDay];
    const haze = HAZE_BY_SKY[scenario.sky];
    const ambient = clamp01(skyPal.ambient * (1 - haze * 0.4));
    const shadowHardness = clamp01((1 - haze) * (scenario.timeOfDay === 'NIGHT' ? 0.4 : 1));
    // Soleil bas à l'aube/au crépuscule, haut en journée, absent la nuit.
    const sunHeight = scenario.timeOfDay === 'DAY' ? 0.25 : scenario.timeOfDay === 'NIGHT' ? 0 : 0.72;
    const sun = {
        x: 0.2 + rand() * 0.6,
        y: sunHeight * horizonY,
        visible: scenario.timeOfDay !== 'NIGHT' && horizonY > 0.05,
    };
    // Au nadir (tangage ≈ 90°), `horizonY` sature à 1 : il n'y a plus de bande sol/ciel, l'image
    // entière est le sol vu de dessus. On répartit alors cibles et décor sur toute la surface, au lieu
    // de les empiler sur le bord bas — où les boîtes, semi-débordantes, se chevauchaient hors cadre.
    const overhead = horizonY >= 0.98;
    // Cibles : le nombre suit la densité cumulée, borné pour rester lisible. Taille réduite en altitude.
    const totalDensity = scenario.targets.reduce((sum, t) => sum + t.density, 0);
    const markerCount = Math.min(12, Math.max(scenario.targets.length, Math.round(totalDensity)));
    const boxScale = clamp01(0.1 - ((scenario.altitudeM - 5) / 395) * 0.06); // 5 m → .10, 400 m → .04
    const targets = [];
    for (let i = 0; i < markerCount && scenario.targets.length > 0; i += 1) {
        const pick = scenario.targets[i % scenario.targets.length];
        if (!pick)
            break;
        const w = boxScale * (0.85 + rand() * 0.3);
        const h = w * 1.35;
        targets.push({
            x: 0.08 + rand() * 0.84,
            // Vue de dessus : plein cadre, avec une marge haute/basse qui garde les boîtes entières.
            // Sinon : dans la bande de sol, entre l'horizon et le bas.
            y: overhead ? 0.12 + rand() * 0.74 : horizonY + rand() * (1 - horizonY) * 0.92,
            w,
            h,
            label: pick.label,
        });
    }
    // Décor : arbres (forêt/périurbain) ou bâtiments (habitat dense/périurbain), semés dans le sol.
    const featureCount = FEATURE_COUNT[scenario.environment];
    const features = [];
    const decorTop = overhead ? 0.06 : horizonY;
    for (let i = 0; i < featureCount; i += 1) {
        const treeEnv = scenario.environment === 'FOREST' ||
            (scenario.environment === 'PERIURBAN' && rand() > 0.5) ||
            (scenario.environment === 'FIELD' && rand() > 0.7);
        features.push({
            x: rand(),
            y: decorTop + rand() * (1 - decorTop),
            r: (treeEnv ? 0.02 : 0.03) * (0.7 + rand() * 0.8),
            kind: treeEnv ? 'tree' : 'building',
        });
    }
    // Menaces aériennes : réparties dans la bande de ciel (ADR-0023). Au nadir (peu de ciel), on
    // occupe le haut du cadre. Vues de loin, leurs boîtes sont petites ; l'essaim (count) en sème
    // plusieurs, borné pour rester lisible.
    const aerialBottom = horizonY >= 0.85 ? 0.9 : horizonY * 0.92;
    const aerialTop = 0.05;
    const threats = [];
    for (const threat of scenario.threats) {
        const instances = Math.min(threat.count, 10);
        for (let k = 0; k < instances && threats.length < 12; k += 1) {
            const w = 0.03 + (threat.sizeInches ? (threat.sizeInches / 60) * 0.025 : 0.008);
            threats.push({
                x: 0.06 + rand() * 0.88,
                y: aerialTop + rand() * Math.max(0.08, aerialBottom - aerialTop),
                w,
                h: w * 0.72,
                label: threat.label ?? threat.type,
                trajectory: threat.trajectory,
            });
        }
    }
    return {
        horizonY,
        sky: [skyPal.top, skyPal.bottom],
        sun,
        ground: GROUND_BY_ENV[scenario.environment],
        environment: scenario.environment,
        ambient,
        haze,
        shadowHardness,
        targets,
        threats,
        features,
    };
}
//# sourceMappingURL=simulation-preview.js.map